import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Heart, Coffee, Gift, ArrowLeft } from 'lucide-react'
import logo from '../assets/images/logo.png'
import bannerLogo from '../assets/images/banner-black-min.png'
import dancingKitty from '../assets/images/dancing-kitty-white-min.gif'
import Footer from '../components/layout/Footer'
import GoogleAd from '../components/common/GoogleAd'
import { trackDonationClick, trackEvent } from '../components/common/GoogleAnalytics'

const SupportUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0)
    document.title = 'Support Us - EasyAppIcon'
    trackEvent('support_page_viewed', { event_category: 'engagement', event_label: 'support' })

    return () => {
      document.title = 'EasyAppIcon - Create Mobile App Icons'
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors duration-200 mb-6 font-medium group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Home</span>
          </Link>

          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="p-2 bg-[#df6e57] rounded-xl shadow-lg">
                <img src={logo} alt="EasyAppIcon Logo" className="w-8 h-8" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">EasyAppIcon</h1>
            </div>
          </div>
        </motion.div>

        {/* Main Content Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Hero Section */}
          <div className="p-8 md:p-12 text-center border-b border-gray-100">
            <div className="flex justify-center mb-6">
              <img
                src={dancingKitty}
                alt="Dancing Kitty"
                className="w-36 h-36 object-contain"
              />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Give Us Some Help
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              EasyAppIcon is free for everyone. Your support helps us keep it that way
              and allows us to continuously improve the tool for developers worldwide.
            </p>
          </div>

          {/* Support Options */}
          <div className="p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Buy Me A Coffee */}
              <motion.div
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200 text-center"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Coffee className="w-10 h-10 text-amber-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Buy Us a Coffee</h3>
                <p className="text-gray-600 text-sm mb-5">
                  A small gesture that makes a big difference.
                </p>
                <a
                  href="https://www.buymeacoffee.com/dg43aLHr6"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDonationClick()}
                  className="inline-block hover:scale-105 transition-transform duration-200"
                >
                  <img
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                    alt="Buy Me A Coffee"
                    className="h-12 w-auto"
                  />
                </a>
              </motion.div>

              {/* View an Ad */}
              <motion.div
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 text-center"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Gift className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">View Our Sponsors</h3>
                <p className="text-gray-600 text-sm mb-5">
                  Just by viewing ads below, you help fund our development.
                </p>
                <div className="inline-flex items-center space-x-2 text-blue-600 font-medium text-sm">
                  <Heart className="w-4 h-4" />
                  <span>Scroll down to support</span>
                </div>
              </motion.div>
            </div>

            {/* Ad Section for Offerwall */}
            <div className="mt-8">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500">Sponsored content - viewing these helps keep EasyAppIcon free</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6 min-h-[300px] flex items-center justify-center">
                <div style={{ width: '100%', maxWidth: '970px', minWidth: '300px', minHeight: '300px' }}>
                  <GoogleAd
                    slot="6173490634"
                    style={{
                      display: 'block',
                      width: '100%',
                      minHeight: '300px'
                    }}
                    format="auto"
                    responsive="true"
                  />
                </div>
              </div>
            </div>

            {/* Second Ad Slot */}
            <div className="mt-8">
              <div className="bg-gray-50 rounded-lg p-6 min-h-[250px] flex items-center justify-center">
                <div style={{ width: '100%', maxWidth: '970px', minWidth: '300px', minHeight: '250px' }}>
                  <GoogleAd
                    slot="2466299289"
                    style={{
                      display: 'block',
                      width: '100%',
                      minHeight: '250px'
                    }}
                    format="auto"
                    responsive="true"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Thank You Footer */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-8 text-center border-t border-gray-100">
            <Heart className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-gray-700 font-medium">
              Thank you for your support! Every bit helps us keep EasyAppIcon free and improving.
            </p>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  )
}

export default SupportUs
